import type { Entry, UnresolvedLink } from 'contentful';
import { toContentfulAsset } from 'components/shared/Media/toMediaAsset';
import type { VideoItemReference } from 'types/sharedComponents';
import type { VideoItemSkeleton } from './types';

type ResolvedFields = Entry<VideoItemSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'];
type LinkFields = Entry<VideoItemSkeleton, 'WITHOUT_LINK_RESOLUTION'>['fields'];

// Per-slot union, because links resolve independently: neither modifier alone describes
// an entry that arrives half-resolved.
export type ResolvableVideoItemFields = { [K in keyof ResolvedFields]: ResolvedFields[K] | LinkFields[K] };

export interface ResolvableVideoItem {
  sys: { id: string };
  fields: ResolvableVideoItemFields;
}

/**
 * Flattens a `videoItem` entry into the `VideoItemReference` `<VideoPlayer>` consumes.
 * An unresolvable preview or aspect ratio drops the entry; an unresolvable uploaded
 * video does not, since `videoUrl` may still carry an external source.
 */
export function toVideoItem(
  entry: ResolvableVideoItem | UnresolvedLink<'Entry'> | undefined,
): VideoItemReference | undefined {
  if (entry == null || !('fields' in entry)) {
    return undefined;
  }

  const { fields, sys } = entry;

  const previewImage = fields.previewImage && toContentfulAsset(fields.previewImage);
  const aspectRatio = fields.aspectRatio && 'fields' in fields.aspectRatio ? fields.aspectRatio.fields : undefined;

  if (!previewImage || !aspectRatio) {
    return undefined;
  }

  return {
    __typename: 'VideoItem',
    sys: { id: sys.id },
    video: fields.video ? toContentfulAsset(fields.video) : undefined,
    videoUrl: fields.videoUrl,
    previewImage,
    overlayLabel: fields.overlayLabel,
    aspectRatio: { aspectRatio: aspectRatio.aspectRatio },
  };
}
